/**
 * Like API
 * Handles all like/unlike API communications
 */

import { ApiBase } from "../core/api-base";
import { notifier } from "../core/notifier/notifier";
import { z } from "zod";
import {
    AddLikeToComment201Response,
    AddLikeToComment400Response,
    AddLikeToComment401Response,
    AddLikeToComment403Response,
    AddLikeToComment404Response,
    AddLikeToComment500Response,
    AddLikeToPost201Response,
    AddLikeToPost400Response,
    AddLikeToPost401Response,
    AddLikeToPost403Response,
    AddLikeToPost404Response,
    AddLikeToPost500Response,
    RemoveLikeFromComment200Response,
    RemoveLikeFromComment400Response,
    RemoveLikeFromComment401Response,
    RemoveLikeFromComment403Response,
    RemoveLikeFromComment404Response,
    RemoveLikeFromComment500Response,
    RemoveLikeFromPost200Response,
    RemoveLikeFromPost400Response,
    RemoveLikeFromPost401Response,
    RemoveLikeFromPost403Response,
    RemoveLikeFromPost404Response,
    RemoveLikeFromPost500Response,
} from "../api/generated/posts.zod";

type UnknownRecord = Record<string, unknown>;

const LikeMutationResponseSchema = z.union([
    AddLikeToPost201Response,
    RemoveLikeFromPost200Response,
    AddLikeToComment201Response,
    RemoveLikeFromComment200Response,
]);

const LikeErrorPayloadSchema = z.union([
    AddLikeToPost400Response,
    AddLikeToPost401Response,
    AddLikeToPost403Response,
    AddLikeToPost404Response,
    AddLikeToPost500Response,
    RemoveLikeFromPost400Response,
    RemoveLikeFromPost401Response,
    RemoveLikeFromPost403Response,
    RemoveLikeFromPost404Response,
    RemoveLikeFromPost500Response,
    AddLikeToComment400Response,
    AddLikeToComment401Response,
    AddLikeToComment403Response,
    AddLikeToComment404Response,
    AddLikeToComment500Response,
    RemoveLikeFromComment400Response,
    RemoveLikeFromComment401Response,
    RemoveLikeFromComment403Response,
    RemoveLikeFromComment404Response,
    RemoveLikeFromComment500Response,
]);

const LikeApiResponseSchema = z.union([LikeMutationResponseSchema, LikeErrorPayloadSchema]);

type LikeGeneratedApiResponse = z.infer<typeof LikeApiResponseSchema>;

type LikeNormalizedApiResponse = {
    success: 1;
    likes_count?: number;
    already_liked?: true;
    already_unliked?: true;
};

export type LikeApiResponse = LikeGeneratedApiResponse | LikeNormalizedApiResponse;

type LikeScope = "post" | "comment";

class LikeApiService extends ApiBase {
    public static isRecord(value: unknown): value is UnknownRecord {
        return !!value && typeof value === "object" && !Array.isArray(value);
    }

    public static buildErrorMessage(result: unknown, fallbackMessage: string, _status: number): string {
        const safeResult = this.isRecord(result) ? result : {};

        const errors = safeResult?.errors;
        let errorText = "";

        if (Array.isArray(errors)) {
            errorText = errors.join("\n");
        } else if (errors && typeof errors === "object") {
            const lines: string[] = [];
            for (const [field, messages] of Object.entries(errors)) {
                if (Array.isArray(messages)) {
                    for (const message of messages) {
                        lines.push(`${field}: ${String(message)}`);
                    }
                } else if (messages !== undefined && messages !== null) {
                    lines.push(`${field}: ${String(messages)}`);
                }
            }
            errorText = lines.join("\n");
        }

        const message = safeResult?.message;
        const error = safeResult?.error;
        return (
            (typeof message === "string" && message) ||
            (typeof error === "string" && error) ||
            errorText ||
            fallbackMessage
        );
    }

    public static async parseLikeResponse(response: Response): Promise<LikeApiResponse> {
        const payload = (await response.json().catch(() => ({}))) as unknown;
        const parsed = LikeApiResponseSchema.safeParse(payload);
        if (parsed.success) {
            return parsed.data;
        }

        return {};
    }

    public static getResponseMessage(result: LikeApiResponse): string {
        const safeResult = this.isRecord(result) ? result : null;
        const message = safeResult?.["message"];
        if (typeof message === "string") {
            return message;
        }

        return "";
    }

    public static async addPostLike(boardId: string, postId: number): Promise<LikeApiResponse> {
        const url = `/api/v1/posts/${boardId}/${String(postId)}/likes`;
        const response = await super.apiPost(url, null, { noSpinner: true });
        const result = await this.parseLikeResponse(response);

        if (!response.ok) {
            const msg = this.getResponseMessage(result).toLowerCase();
            if (response.status === 400 && msg.includes("already liked")) {
                return { success: 1, already_liked: true, likes_count: result?.likes_count };
            }
            notifier.error(this.buildErrorMessage(result, "like_post_failed", response.status));
            throw new Error(
                this.getResponseMessage(result) || `Server error (${response.status}): ${response.statusText}`,
            );
        }
        return result;
    }

    public static async deletePostLike(boardId: string, postId: number): Promise<LikeApiResponse> {
        const url = `/api/v1/posts/${boardId}/${String(postId)}/likes`;
        const response = await super.apiDelete(url, { noSpinner: true });
        const result = await this.parseLikeResponse(response);

        if (!response.ok) {
            const msg = this.getResponseMessage(result).toLowerCase();
            if (response.status === 400 && (msg.includes("already unliked") || msg.includes("not liked"))) {
                return { success: 1, already_unliked: true, likes_count: result?.likes_count };
            }
            notifier.error(this.buildErrorMessage(result, "unlike_post_failed", response.status));
            throw new Error(
                this.getResponseMessage(result) || `Server error (${response.status}): ${response.statusText}`,
            );
        }
        return result;
    }

    public static async addCommentLike(boardId: string, postId: number, commentId: number): Promise<LikeApiResponse> {
        const url = `/api/v1/posts/${boardId}/${String(postId)}/comments/${String(commentId)}/likes`;
        const response = await super.apiPost(url, null, { noSpinner: true });
        const result = await this.parseLikeResponse(response);

        if (!response.ok) {
            const msg = this.getResponseMessage(result).toLowerCase();
            if (response.status === 400 && msg.includes("already liked")) {
                return { success: 1, already_liked: true, likes_count: result?.likes_count };
            }
            notifier.error(this.buildErrorMessage(result, "like_comment_failed", response.status));
            throw new Error(
                this.getResponseMessage(result) || `Server error (${response.status}): ${response.statusText}`,
            );
        }
        return result;
    }

    public static async deleteCommentLike(
        boardId: string,
        postId: number,
        commentId: number,
    ): Promise<LikeApiResponse> {
        const url = `/api/v1/posts/${boardId}/${String(postId)}/comments/${String(commentId)}/likes`;
        const response = await super.apiDelete(url, { noSpinner: true });
        const result = await this.parseLikeResponse(response);

        if (!response.ok) {
            const msg = this.getResponseMessage(result).toLowerCase();
            if (response.status === 400 && (msg.includes("already unliked") || msg.includes("not liked"))) {
                return { success: 1, already_unliked: true, likes_count: result?.likes_count };
            }
            notifier.error(this.buildErrorMessage(result, "unlike_comment_failed", response.status));
            throw new Error(
                this.getResponseMessage(result) || `Server error (${response.status}): ${response.statusText}`,
            );
        }
        return result;
    }

    public static async performLikeActionWithRetry(
        isLiked: boolean,
        boardId: string,
        postId: number,
        commentId: number | undefined,
        scope: LikeScope | string,
        maxRetries = 2,
    ): Promise<LikeApiResponse> {
        let lastError: unknown = null;

        for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
            try {
                let response: LikeApiResponse;
                if (scope === "comment") {
                    const normalizedCommentId = Number.isInteger(commentId) && commentId > 0 ? commentId : 0;
                    if (isLiked) {
                        response = await this.deleteCommentLike(boardId, postId, normalizedCommentId);
                    } else {
                        response = await this.addCommentLike(boardId, postId, normalizedCommentId);
                    }
                } else if (isLiked) {
                    response = await this.deletePostLike(boardId, postId);
                } else {
                    response = await this.addPostLike(boardId, postId);
                }

                return response;
            } catch (error) {
                lastError = error;
                if (attempt < maxRetries) {
                    await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
                }
            }
        }

        throw lastError ?? new Error("Like action failed");
    }
}

export { LikeApiService };
